/**
 * URL tenant ↔ session tenantScope bind (ADM-TKT-0104 / G4).
 *
 * Session exposes tenantScope as tenant IDs (+ homeTenantCode for the bound home).
 * Multi-grant sessions also receive tenantScopeCodes from GET /admin/me.
 * Bind rules:
 * - tenantScope `*` → allow any URL tenant
 * - empty scope → deny
 * - length === 1 → URL must match homeTenantCode (deny-default when home unknown)
 * - length > 1 → URL must be in tenantScopeCodes (fail-closed if codes empty/missing)
 */

export interface UrlTenantScopeBindInput {
  readonly urlTenantCode: string;
  readonly homeTenantCode: string | null;
  readonly tenantScope: number[] | '*';
  /**
   * Codes for home + grants from GET /admin/me.
   * Required for multi-grant (length > 1) bind — empty/missing fails closed.
   */
  readonly tenantScopeCodes?: readonly string[] | null;
}

export function isUrlTenantInSessionScope(input: UrlTenantScopeBindInput): boolean {
  const url = input.urlTenantCode.trim().toLowerCase();
  if (url.length === 0) {
    return false;
  }

  if (input.tenantScope === '*') {
    return true;
  }

  if (Array.isArray(input.tenantScope) && input.tenantScope.length === 0) {
    return false;
  }

  if (Array.isArray(input.tenantScope) && input.tenantScope.length > 1) {
    const codes = input.tenantScopeCodes;
    if (codes == null || codes.length === 0) {
      // Fail closed: multi-grant without /me codes must not allow any URL tenant.
      return false;
    }
    return codes.some((code) => code.trim().toLowerCase() === url);
  }

  const home = input.homeTenantCode?.trim().toLowerCase() ?? '';
  if (home.length === 0) {
    // Single-tenant bound session without home code metadata — deny-default.
    return false;
  }

  return home === url;
}
